import React, { useEffect, useMemo, useState, useRef } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useSidebar } from '../SidebarSelfServe/SidebarContext'
import { useHeader } from '../HeaderSelfServe/HeaderContext'
import { addAuthParamsToCampaignBuilderUrl } from '../Country/Page/Pipeline/PipelineView/utils'
import useDesignManagement from './hooks/useDesignManagement'
import { authorization } from '../../Module/Auth/Auth'
import { CONFIG } from '../../Config/config'

const DesignEditor = ({ history }) => {
  const [loading, setLoading] = useState(false)
  // get from query params - use useLocation hook for reactive updates
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const user = authorization.getUser()
  const action = searchParams.get('action')
  const baseDocumentId = searchParams.get('baseDocumentId')
  const fileLink = searchParams.get('fileLink')
  const hasHtmlContent = searchParams.get('hasHtmlContent')
  const clientId = user.client ? Number(user.client.id) : null
  const { designId } = useParams()
  const { setOpen } = useSidebar()
  const { setIsVisible } = useHeader()

  const { designs, getDesign } = useDesignManagement({
    clientId,
  })
  const design = designs.find(design => design.id === designId) || {}
  const format = design.format || 'TEARAWAY'
  const countryCode = user.countryCode || 'US'

  // State to store the full design data when loaded
  const [fullDesignData, setFullDesignData] = useState(null)
  // Track if we're checking for existing design (to prevent iframe from loading prematurely)
  const [isCheckingDesign, setIsCheckingDesign] = useState(false)
  // Track if we've already checked this design to prevent loops
  const hasCheckedDesign = useRef(false)

  // Force iframe reload by changing key
  const [iframeKey, setIframeKey] = useState(Date.now())

  useEffect(() => {
    setOpen(false)
    setIsVisible(false)
    return () => {
      setOpen(true)
      setIsVisible(true)
    }
  }, [])

  // Reset check flag when designId or action changes
  useEffect(() => {
    hasCheckedDesign.current = false
  }, [designId, action])

  // Check if design already exists when action is 'create' and redirect to edit if it does
  // This check ONLY runs when action=create, NOT when action=edit
  useEffect(() => {
    const checkAndRedirectToEdit = async () => {
      // Only check if action is 'create' and we don't already have fileLink
      // Also check if we haven't already checked this design to prevent loops
      // IMPORTANT: Skip all checks if action is 'edit' - let it work normally
      if (
        action === 'create' &&
        !fileLink &&
        designId &&
        getDesign &&
        !hasCheckedDesign.current
      ) {
        hasCheckedDesign.current = true
        setIsCheckingDesign(true)
        try {
          // Check if design exists and has content
          const designData = await getDesign(designId)

          // Check if design has s3Link or if format is different from TEARAWAY
          const hasContent = designData && designData.s3Link
          const hasDifferentFormat =
            designData &&
            designData.format &&
            designData.format !== 'TEARAWAY' &&
            designData.format.toUpperCase() !== 'TEARAWAY'

          // If design exists and has content OR has a different format, redirect to edit mode
          if (designData && (hasContent || hasDifferentFormat)) {
            const hasHtml =
              !!designData.htmlContent && !!designData.htmlContent.length

            // Store the design data first
            setFullDesignData(designData)

            // Update URL to edit mode with correct parameters
            const newSearchParams = new URLSearchParams({
              action: 'edit',
              fileLink: designData.s3Link || '',
              hasHtmlContent: hasHtml.toString(),
            })

            // Preserve baseDocumentId if it exists
            if (baseDocumentId) {
              newSearchParams.set('baseDocumentId', baseDocumentId)
            }

            // Update the URL without page reload
            history.replace({
              pathname: window.location.pathname,
              search: newSearchParams.toString(),
            })
          } else if (designData) {
            // Design exists but doesn't have s3Link and format is TEARAWAY - still store the format
            // But don't redirect, just store it and continue with create mode
            setFullDesignData(designData)
          }
        } catch (error) {
          console.error('Error checking design:', error)
          // If error occurs, assume it's a new design - continue with create mode
        } finally {
          setIsCheckingDesign(false)
        }
      }
      // No else clause - when action=edit, do nothing, let it work normally
    }

    checkAndRedirectToEdit()
  }, [
    action,
    fileLink,
    designId,
    getDesign,
    history,
    baseDocumentId,
    location.search,
  ])

  // Handle messages from the iframe
  useEffect(() => {
    const handleMessage = event => {
      try {
        const { type, data } = event.data

        if (type === 'save') {
          // Handle saving - navigate to design management
          const { designId: savedDesignId } = data

          // Navigate to design management page
          history.push(
            `/dashboard/design-management?savedDesignId=${savedDesignId}`,
          )
        }
      } catch (error) {
        console.error('Error handling message from iframe:', error)
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [history])

  // Use fullDesignData if available, otherwise fall back to design from list or query params
  // Only use fullDesignData when action=create (to preserve format), otherwise use normal values
  const effectiveFormat =
    action === 'create' && fullDesignData && fullDesignData.format
      ? fullDesignData.format
      : format
  const effectiveFileLink =
    action === 'create' && fullDesignData && fullDesignData.s3Link
      ? fullDesignData.s3Link
      : fileLink
  const effectiveHasHtmlContent =
    action === 'create' && fullDesignData && fullDesignData.htmlContent
      ? (
          !!fullDesignData.htmlContent && !!fullDesignData.htmlContent.length
        ).toString()
      : hasHtmlContent

  const url = useMemo(() => {
    let designUrl = ''

    // Check if we're at /builder URL and treat as edit action
    const isAtBuilderUrl = window.location.pathname.includes('/builder')
    const effectiveAction = isAtBuilderUrl ? 'edit' : action

    if (effectiveAction === 'create') {
      // If we have the format from fullDesignData, include it in the URL
      // This ensures the format is preserved on refresh
      if (effectiveFormat && effectiveFormat !== 'TEARAWAY') {
        designUrl = `${
          CONFIG.SS_BASE_URL
        }/design/${countryCode}/${designId}/${effectiveFormat}?variant=integrated&action=create`
      } else {
        designUrl = `${
          CONFIG.SS_BASE_URL
        }/design/${countryCode}/${designId}?variant=integrated&action=create`
      }
    }

    if (effectiveAction === 'edit') {
      // Build edit URL - fileLink is optional if format is different from TEARAWAY
      if (effectiveFileLink) {
        // Normal edit mode with fileLink
        designUrl = `${
          CONFIG.SS_BASE_URL
        }/design/${countryCode}/${designId}/${effectiveFormat}/builder?variant=integrated&action=edit&fileLink=${effectiveFileLink}&hasHtmlContent=${effectiveHasHtmlContent ||
          'false'}`
      } else if (
        effectiveFormat &&
        effectiveFormat !== 'TEARAWAY' &&
        effectiveFormat.toUpperCase() !== 'TEARAWAY'
      ) {
        // Edit mode without fileLink but with format different from TEARAWAY
        // This happens when format was changed but design hasn't been saved yet
        designUrl = `${
          CONFIG.SS_BASE_URL
        }/design/${countryCode}/${designId}/${effectiveFormat}/builder?variant=integrated&action=edit&fileLink=&hasHtmlContent=false`
      } else {
        // If we don't have fileLink and format is TEARAWAY, we might be loading
        // Return empty string to prevent iframe from loading with incomplete data
        return ''
      }
    }

    if (effectiveAction === 'preview') {
      designUrl = `${
        CONFIG.SS_BASE_URL
      }/design/${countryCode}/${designId}/${format}/builder?variant=integrated&action=preview`
    }

    if (effectiveAction === 'duplicate') {
      designUrl = `${
        CONFIG.SS_BASE_URL
      }/design/${countryCode}/${designId}/${format}/builder?baseDocumentId=${baseDocumentId}&variant=integrated&action=duplicate`
    }

    if (!effectiveAction) {
      designUrl = `${
        CONFIG.SS_BASE_URL
      }/design/${countryCode}/${designId}/${format}/builder?variant=integrated`
    }

    const linkWithAuth = addAuthParamsToCampaignBuilderUrl(designUrl, clientId)

    // Add multiple cache busting parameters
    const cacheBustParams = new URLSearchParams({
      cacheBust: Date.now().toString(),
      timestamp: Date.now().toString(),
      random: Math.random().toString(),
      session: sessionStorage.getItem('sessionId') || Date.now().toString(),
    })

    return `${linkWithAuth}&${cacheBustParams.toString()}`
  }, [
    designId,
    clientId,
    action,
    baseDocumentId,
    effectiveFormat,
    countryCode,
    effectiveFileLink,
    effectiveHasHtmlContent,
    format,
  ])

  // Force iframe reload when URL changes
  useEffect(() => {
    setIframeKey(Date.now())
  }, [url])

  // Show loading if we're loading, checking design (only for create mode), or if URL is not ready yet
  if (loading || (isCheckingDesign && action === 'create') || !url) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100vh',
        }}
      >
        <div style={{ flex: 1, width: '100%' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <div
              style={{
                animation: 'spin 1s linear infinite',
                borderRadius: '50%',
                height: '48px',
                width: '48px',
                borderTop: '2px solid #111827',
                borderBottom: '2px solid #111827',
                borderLeft: '2px solid transparent',
                borderRight: '2px solid transparent',
              }}
            />
          </div>
        </div>
      </div>
    )
  }
  return (
    <main
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100vh',
      }}
    >
      <div style={{ flex: 1, width: '100%' }}>
        <iframe
          key={iframeKey}
          src={url}
          style={{ width: '100%', height: '100%', border: 0 }}
          title="Design Editor"
          allow="clipboard-read; clipboard-write; *"
          data-cache-bust={Date.now()}
          onLoad={() => {
            setLoading(false)
          }}
        />
      </div>
    </main>
  )
}

export default DesignEditor
